/**
 * Results cards swiper — left-aligned first slide, loop, green segment pagination, trackpad wheel.
 */
(function () {
  const ROOT = '[data-results-cards-swiper-section]';
  const SWIPER_CSS = 'link[href*="swiper-bundle"]';
  const CLONE_ATTR = 'data-rmcs-loop-clone';
  const DESKTOP_MQ = '(min-width: 990px)';

  function isDesktopLayout() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function destroy(root) {
    if (root._rmcsFixTimer) {
      window.clearTimeout(root._rmcsFixTimer);
      root._rmcsFixTimer = null;
    }
    if (root._resultsCardsSwiper) {
      root._resultsCardsSwiper.destroy(true, true);
      root._resultsCardsSwiper = null;
    }
    const el = root.querySelector('.results-cards-swiper__swiper');
    if (el) {
      delete el.dataset.swiperInitialized;
      removeLoopClones(el);
    }
  }

  function removeLoopClones(swiperEl) {
    const wrapper = swiperEl && swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;
    wrapper.querySelectorAll(`.swiper-slide[${CLONE_ATTR}]`).forEach((node) => node.remove());
  }

  function whenSwiperCssReady(callback) {
    const link = document.querySelector(SWIPER_CSS);
    if (!link) {
      callback();
      return;
    }
    if (link.sheet) {
      callback();
      return;
    }
    link.addEventListener('load', callback, { once: true });
    window.setTimeout(callback, 600);
  }

  function estimateSlideWidth(root, swiperEl) {
    const slide = swiperEl.querySelector('.swiper-slide');
    if (slide) {
      const measured = slide.getBoundingClientRect().width;
      if (measured > 0) return measured;
    }

    const styles = getComputedStyle(root);
    const maxLg = parseInt(styles.getPropertyValue('--rmcs-slide-max-lg'), 10) || 680;
    const maxSm = parseInt(styles.getPropertyValue('--rmcs-slide-max'), 10) || 420;
    const vw = swiperEl.clientWidth || window.innerWidth;

    if (window.matchMedia(DESKTOP_MQ).matches) {
      return Math.min(vw * 0.88, maxLg);
    }

    const peek =
      parseFloat(getComputedStyle(root).getPropertyValue('--rmcs-peek')) * 16 || 52;
    return Math.max(vw - peek, 200);
  }

  function getMinSlidesForLoop(containerWidth, slideWidth, spaceBetween, realCount) {
    const w = Math.max(1, Math.ceil(containerWidth / (slideWidth + spaceBetween)));
    const loopedSlides = Math.max(w, Math.ceil(w / 2));
    const swiperMin = w + loopedSlides + 1;
    const safeMin = realCount > 1 ? realCount * 2 + 1 : swiperMin;
    return Math.max(swiperMin, safeMin);
  }

  function padSlidesForLoop(root, swiperEl, spaceBetween) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return 0;

    removeLoopClones(swiperEl);

    const originals = [...wrapper.querySelectorAll('.swiper-slide')];
    const realCount = originals.length;
    if (realCount <= 1) return realCount;

    const containerWidth = swiperEl.clientWidth;
    const slideWidth = estimateSlideWidth(root, swiperEl);
    if (!containerWidth || !slideWidth) return realCount;

    const minSlides = getMinSlidesForLoop(containerWidth, slideWidth, spaceBetween, realCount);
    let total = realCount;
    let cloneIndex = 0;

    while (total < minSlides) {
      const source = originals[cloneIndex % realCount];
      const clone = source.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      wrapper.appendChild(clone);
      total += 1;
      cloneIndex += 1;
    }

    root.dataset.realSlideCount = String(realCount);
    return realCount;
  }

  function fixLoopPosition(swiper, realSlideCount) {
    if (!swiper || swiper.destroyed) return;

    swiper.update();

    if (!swiper.params.loop) return;

    if (typeof swiper.loopFix === 'function') swiper.loopFix();
    if (typeof swiper.slideToLoop === 'function') swiper.slideToLoop(0, 0);
    swiper.update();
    if (typeof swiper.loopFix === 'function') swiper.loopFix();
  }

  function scheduleLoopFix(root, swiper, realSlideCount) {
    if (!swiper || swiper.destroyed) return;

    const run = () => fixLoopPosition(swiper, realSlideCount);

    run();
    window.requestAnimationFrame(() => {
      run();
      window.requestAnimationFrame(run);
    });

    if (root._rmcsFixTimer) window.clearTimeout(root._rmcsFixTimer);
    root._rmcsFixTimer = window.setTimeout(run, 150);
  }

  function buildOptions(root, realSlideCount, spaceBetween, paginationEl) {
    function bulletMarkup(index, className) {
      return `<span class="${className} rmcs-pagination__bullet" role="presentation" data-index="${index}"></span>`;
    }

    const swiperEl = root.querySelector('.results-cards-swiper__swiper');

    const mousewheel = {
      enabled: true,
      forceToAxis: false,
      sensitivity: 1.15,
      thresholdDelta: 12,
      thresholdTime: 400,
      releaseOnEdges: true,
      eventsTarget: swiperEl,
    };

    const options = {
      slidesPerView: 'auto',
      centeredSlides: false,
      spaceBetween,
      speed: 480,
      grabCursor: true,
      watchSlidesProgress: true,
      watchOverflow: false,
      observer: true,
      observeParents: true,
      mousewheel,
      on: {
        afterInit(swiper) {
          scheduleLoopFix(root, swiper, realSlideCount);
        },
      },
    };

    if (realSlideCount > 1 && paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
        renderBullet(index, className) {
          if (index >= realSlideCount) return '';
          return bulletMarkup(index, className);
        },
      };
    }

    if (realSlideCount > 1) {
      options.loop = true;
      options.loopAdditionalSlides = 0;
    }

    return options;
  }

  function init(root, attempt) {
    const swiperEl = root.querySelector('.results-cards-swiper__swiper');
    if (!swiperEl || swiperEl.dataset.swiperInitialized === '1') return;

    const n = typeof attempt === 'number' ? attempt : 0;
    if (typeof window.Swiper === 'undefined') {
      if (n < 80) window.setTimeout(() => init(root, n + 1), 50);
      return;
    }

    if (!swiperEl.clientWidth && n < 24) {
      window.requestAnimationFrame(() => init(root, n + 1));
      return;
    }

    const spaceBetween = parseInt(root.dataset.spaceBetween || '16', 10) || 16;
    const paginationEl = root.querySelector('.results-cards-swiper__pagination');

    const realSlideCount = padSlidesForLoop(root, swiperEl, spaceBetween);
    if (realSlideCount === 0) return;
    if (!swiperEl.querySelectorAll('.swiper-slide').length) return;

    const options = buildOptions(root, realSlideCount, spaceBetween, paginationEl);

    try {
      root._resultsCardsSwiper = new window.Swiper(swiperEl, options);
      swiperEl.dataset.swiperInitialized = '1';
    } catch (e) {
      removeLoopClones(swiperEl);
      const fallback = buildOptions(root, realSlideCount, spaceBetween, paginationEl);
      fallback.loop = false;
      fallback.rewind = true;
      delete fallback.loopAdditionalSlides;
      root._resultsCardsSwiper = new window.Swiper(swiperEl, fallback);
      swiperEl.dataset.swiperInitialized = '1';
    }
  }

  function reinit(root) {
    destroy(root);
    init(root);
  }

  function initFromSection(sectionEl) {
    const root = sectionEl.matches(ROOT) ? sectionEl : sectionEl.querySelector(ROOT);
    if (!root) return;
    whenSwiperCssReady(() => reinit(root));
  }

  function boot() {
    whenSwiperCssReady(() => {
      document.querySelectorAll(ROOT).forEach((root) => reinit(root));
    });
  }

  document.addEventListener('shopify:section:load', (ev) => {
    const id = ev.detail && ev.detail.sectionId;
    if (!id) return;
    const section = document.querySelector(`#shopify-section-${id}`);
    if (section) initFromSection(section);
  });

  document.addEventListener('shopify:section:unload', (ev) => {
    const id = ev.detail && ev.detail.sectionId;
    if (!id) return;
    const section = document.querySelector(`#shopify-section-${id}`);
    const root = section && section.querySelector(ROOT);
    if (root) destroy(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      const swiper = root._resultsCardsSwiper;
      const realSlideCount = parseInt(root.dataset.realSlideCount || '0', 10);
      if (swiper && realSlideCount > 0) {
        scheduleLoopFix(root, swiper, realSlideCount);
      }
    });
  });

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll(ROOT).forEach((root) => {
        const swiper = root._resultsCardsSwiper;
        const realSlideCount = parseInt(root.dataset.realSlideCount || '0', 10);
        if (swiper && realSlideCount > 0) {
          fixLoopPosition(swiper, realSlideCount);
        } else {
          reinit(root);
        }
      });
    }, 200);
  });
})();
